import React, { useEffect } from 'react';
import { FaTwitch, FaYoutube, FaTwitter, FaInstagram, FaTiktok } from 'react-icons/fa';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Reveal from '../components/ui/Reveal';
import Button from '../components/ui/Button';
import TiltCard from '../components/ui/TiltCard';
import talents from '../data/talents.json';
import './TalentRosterPage.css';

const SOCIAL_ICONS = {
  twitch: FaTwitch,
  youtube: FaYoutube,
  youtube2: FaYoutube,
  twitter: FaTwitter,
  instagram: FaInstagram,
  tiktok: FaTiktok,
};

const socialLabel = (platform) =>
  platform === 'youtube2' ? 'Second YouTube channel' : platform;

const renderCard = (t, i) => (
  <TiltCard className="roster-card" key={t.id} delay={(i % 3) * 0.08}>
    <div className="roster-card__image">
      <img src={process.env.PUBLIC_URL + t.image} alt={t.name} loading="lazy" />
      <div className="roster-card__overlay" />
      <span className="roster-card__reach">{t.reach} reach</span>
    </div>
    <div className="roster-card__info">
      <h3 className="roster-card__name">{t.name}</h3>
      <span className="roster-card__category">{t.category}</span>
      {t.bio && <p className="roster-card__bio">{t.bio}</p>}
      <div className="roster-card__socials">
        {Object.entries(t.socials).map(([platform, url]) => {
          const Icon = SOCIAL_ICONS[platform];
          if (!Icon) return null;
          return (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={socialLabel(platform)}
            >
              <Icon />
            </a>
          );
        })}
      </div>
    </div>
  </TiltCard>
);

const TalentRosterPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const exclusive = talents.filter((t) => t.exclusive);
  const nonExclusive = talents.filter((t) => !t.exclusive);

  return (
    <>
      <>
        <title>Our Creators — ThirdRail Avenue</title>
        <meta name="description" content="Meet the roster of Clash Royale creators ThirdRail Avenue represents and partners with." />
      </>

      <Navbar />

      <header className="roster-hero" id="main-content">
        <div className="roster-hero__glow" />
        <div className="tra-container">
          <Reveal as="span" className="tra-eyebrow">Our Roster</Reveal>
          <Reveal as="h1" className="roster-hero__title" delay={0.05}>
            The voices shaping <span className="tra-gradient-text">gaming culture</span>
          </Reveal>
          <Reveal as="p" className="roster-hero__sub" delay={0.1}>
            A hand-picked roster of gaming's most engaging creators — exclusive talent we
            manage end-to-end, plus trusted partners — with a combined reach of over 4 million.
          </Reveal>
        </div>
      </header>

      <section className="roster tra-container">
        <div className="roster__tier">
          <div className="roster__tier-head">
            <Reveal as="span" className="tra-eyebrow">Exclusive Talent</Reveal>
            <Reveal as="h2" className="roster__tier-title" delay={0.05}>Creators we represent</Reveal>
            <Reveal as="p" className="roster__tier-sub" delay={0.1}>
              Talent we manage end-to-end — strategy, sponsorships, and content.
            </Reveal>
          </div>
          <div className="roster__grid">
            {exclusive.map(renderCard)}
          </div>
        </div>

        <div className="roster__tier">
          <div className="roster__tier-head">
            <Reveal as="span" className="tra-eyebrow">Non-Exclusive Talent</Reveal>
            <Reveal as="h2" className="roster__tier-title" delay={0.05}>Creators we partner with</Reveal>
            <Reveal as="p" className="roster__tier-sub" delay={0.1}>
              Trusted creators we collaborate with on brand campaigns.
            </Reveal>
          </div>
          <div className="roster__grid">
            {nonExclusive.map(renderCard)}
          </div>
        </div>

        <div className="roster__cta">
          <h2>Think you're a fit for the roster?</h2>
          <Button to="/#contact" variant="primary" size="lg" arrow>Apply To Join</Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TalentRosterPage;
